import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "../typography";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AtoZStatus,
  getCandidateAtoZStatus,
} from "../../utility/application-data";
import { getCandidateData } from "../../utility/candidate-data";
import { StatusEnum } from "../../hoc/with-status";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Tag } from "@amzn/stencil-react-components/tag";
import { getLocale } from "../../utility/locale-helper";
import useArb from "../../hooks/use-arb";
import { addEventMetric } from "../../utility/adobe-analytics";
import { Link } from "@amzn/stencil-react-components/link";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";

const ADOBE_EVENT_SETUP_ATOZ_ACCOUNT = "Click Setup your AtoZ Account";
const ADOBE_EVENT_VIEW_ATOZ_ACCOUNT = "View your AtoZ Account";
const ATOZ_STATUS_COMPLETED = "Completed";
const ATOZ_STATUS_ACTION_REQUIRED = "Action Required";
const ATOZ_STATUS_NOT_AVAILABLE_YET = "Not Available Yet";
const ATOZ_STATUS_STATUS_ERROR = "Error";

export interface UrlParams {
  applicationId: string;
}

export interface AtoZTaskParams {
  status: StatusEnum;
  href: string;
}

export enum AtoZStatusButtonState {
  Active = "Active",
  Hidden = "Hidden",
  Disabled = "Disabled",
}

const locale = getLocale();

export const AtoZStatusTask = ({ status, href }: AtoZTaskParams) => {
  const { applicationId } = useParams<UrlParams>();

  const [loading, setLoading] = useState(true);

  const bundle = useArb("HVHChecklistPantherResource");
  const atoZLoadingText = bundle.getMessage("Checklist-AtoZ-Loading-Message");

  const atoZSetupAccountNotAvailableDetails1 = bundle.getMessage(
    "Checklist-AtoZ-Not-Available-Details1"
  );

  const atoZSetupAccountNotAvailableDetails2 = bundle.getMessage(
    "Checklist-AtoZ-Not-Available-Details2"
  );

  const atoZViewAtoZUserNameMessage = bundle.getMessage(
    "Checklist-AtoZ-Show-UserName"
  );

  const atoZViewAtoZAccountButton = bundle.getMessage(
    "Checklist-AtoZ-View-Button"
  );

  const atoZSetupAZAccountMessage = bundle.getMessage(
    "Checklist-AtoZ-Setup-Message"
  );

  const atoZSetupAZAccountButton = bundle.getMessage(
    "Checklist-AtoZ-Setup-Button"
  );

  const atozUsername = bundle.getMessage("Checklist-AtoZ-Username");

  const [atoZStatusLoginSet, setAtoZStatusLoginSet] = useState(false);

  const [atozCandidateStatus, setAtoZCandidateStatus] = useState<string>();

  let atoZBodyText = atoZSetupAccountNotAvailableDetails2;
  let atoZButtonText;

  const [loadingButton, setLoadingButton] = useState(false);

  const [
    atoZButtonStatus,
    setAtoZButtonStatus,
  ] = useState<AtoZStatusButtonState>(AtoZStatusButtonState.Active);

  const [
    atoZShowUserName,
    setAtoZShowUserName,
  ] = useState<AtoZStatusButtonState>(AtoZStatusButtonState.Hidden);

  const [atoZEmployeeLogin, setAtoZEmployeeLogin] = useState<string>();

  const requestAtoZLoginStatus = useCallback(async () => {
    setLoading(true);
    setAtoZButtonStatus(AtoZStatusButtonState.Active);

    const candidateData = await getCandidateData(applicationId);
    let response = {} as AtoZStatus;
    if (candidateData && candidateData.sfCandidateId) {
      try {
        response = await getCandidateAtoZStatus(
          href,
          candidateData.sfCandidateId,
          applicationId,
          locale,
          "false"
        );
      } catch (e) {
        if (!atoZButtonStatus) {
          setAtoZButtonStatus(AtoZStatusButtonState.Hidden);
        }
        setAtoZCandidateStatus(ATOZ_STATUS_STATUS_ERROR);
      }

      if (response && response.errorCode) {
        if (!atoZButtonStatus) {
          setAtoZButtonStatus(AtoZStatusButtonState.Hidden);
        }
        setAtoZCandidateStatus(ATOZ_STATUS_STATUS_ERROR);
      } else if (response && response.isAccountReady) {
        setAtoZButtonStatus(AtoZStatusButtonState.Active);
        setAtoZEmployeeLogin(response.employeeLogin);
        setAtoZShowUserName(AtoZStatusButtonState.Active);
        if (response.isPasswordSet)
          setAtoZCandidateStatus(ATOZ_STATUS_COMPLETED);
        else setAtoZCandidateStatus(ATOZ_STATUS_ACTION_REQUIRED);
      } else {
        setAtoZButtonStatus(AtoZStatusButtonState.Hidden);
        setAtoZCandidateStatus(ATOZ_STATUS_NOT_AVAILABLE_YET);
      }
    }

    setLoading(false);
  }, [atoZButtonStatus, atozCandidateStatus]);

  useEffect(() => {
    requestAtoZLoginStatus();
  }, []);

  const sendAnalyticsEvent = useCallback(
    async (eventName: string) => {
      await addEventMetric(window, applicationId, eventName);
    },
    [applicationId]
  );

  const requestAtoZLoginUrl = useCallback(async () => {
    setLoadingButton(true);
    const candidateData = await getCandidateData(applicationId);

    let response = {} as AtoZStatus;
    if (candidateData && candidateData.sfCandidateId) {
      response = await getCandidateAtoZStatus(
        href,
        candidateData.sfCandidateId,
        applicationId,
        locale,
        "true"
      );

      if (status == ATOZ_STATUS_COMPLETED) {
        sendAnalyticsEvent(ADOBE_EVENT_VIEW_ATOZ_ACCOUNT);
      } else {
        sendAnalyticsEvent(ADOBE_EVENT_SETUP_ATOZ_ACCOUNT);
      }

      if (!atoZStatusLoginSet) {
        window.open(response.aToZLoginUrl, "_blank");
        setAtoZStatusLoginSet(true);
      } else {
        setAtoZStatusLoginSet(false);
      }
    }

    setLoadingButton(false);
  }, [atoZStatusLoginSet]);

  const redirect = useCallback(() => {
    requestAtoZLoginUrl();
  }, []);

  const onRetry = useCallback(() => {
    requestAtoZLoginStatus();
    setAtoZButtonStatus(AtoZStatusButtonState.Disabled);
  }, [atozCandidateStatus, atoZButtonStatus]);

  const shouldShowButton =
    atoZButtonStatus === AtoZStatusButtonState.Active ||
    status === StatusEnum.API_ERROR ||
    status === StatusEnum.COMPLETED ||
    status === StatusEnum.INELIGIBLE;

  const shouldShowUserName = atoZShowUserName === AtoZStatusButtonState.Active;

  const isButtonDisabled = atoZButtonStatus === AtoZStatusButtonState.Disabled;

  if (atozCandidateStatus == ATOZ_STATUS_COMPLETED) {
    atoZBodyText = atoZViewAtoZUserNameMessage;
    atoZButtonText = atoZViewAtoZAccountButton;
  } else if (atozCandidateStatus == ATOZ_STATUS_ACTION_REQUIRED) {
    atoZBodyText = atoZSetupAZAccountMessage;
    atoZButtonText = atoZSetupAZAccountButton;
  } else if (atozCandidateStatus == ATOZ_STATUS_STATUS_ERROR) {
    const retry = bundle.getMessage("Checklist-General-RetryButtonText");
    const supportLinkUrl = window.location.origin + "/contact-us";
    const here = bundle.getMessage("Checklist-MainPage-Link");
    const supportLink = (
      <Link target="_blank" href={supportLinkUrl}>
        {here}
      </Link>
    );
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>
          <Text>
            {bundle.formatMessage("Checklist-MainPage-I9-E400_500", {
              supportLink: supportLink,
            })}
          </Text>
        </MessageBanner>
        <Row>
          <View margin="4px">
            <Button
              size={ButtonSize.Small}
              onClick={onRetry}
              disabled={isButtonDisabled}
            >
              {retry}
            </Button>
          </View>
        </Row>
      </Col>
    );
  }

  if (loading) {
    return (
      <Spinner
        size={SpinnerSize.Small}
        loadingText={atoZLoadingText}
        showText={true}
      ></Spinner>
    );
  }

  return (
    <Text>
      {!shouldShowButton && (
        <strong>
          {atoZSetupAccountNotAvailableDetails1}
          <br />
          <br />
        </strong>
      )}

      {atoZBodyText}
      <br />
      <br />
      {shouldShowUserName && (
        <Row>
          <Tag>
            Amazon {atozUsername}: {atoZEmployeeLogin}{" "}
          </Tag>
        </Row>
      )}
      <br />
      <View margin="4px">
        {shouldShowButton && (
          <Button
            size={ButtonSize.Small}
            variant={ButtonVariant.Primary}
            onClick={redirect}
            disabled={isButtonDisabled}
            icon={
              loadingButton && (
                <Spinner
                  size={SpinnerSize.Small}
                  loadingText={atoZLoadingText}
                  showText={false}
                ></Spinner>
              )
            }
          >
            {atoZButtonText}
          </Button>
        )}
      </View>
    </Text>
  );
};
